import fe_config from "constants/Configs";
import Constants from "constants/index";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useDispatch, useStore } from "react-redux";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import login from "containers/login";
import SsoLogin from "containers/login/SsoLogin";
import get from "lodash/get";
import ForgotPassword from "containers/forgetPassword";
import resetPassword from "containers/resetPassword";
import TokenManager from "utils/TokenManager";
import ConfirmationBox from "components/ConfirmationBox";
import ErrorBoundary from "components/ErrorBoundry";
import { trackEvent } from "utils/loggly-tracker";
import { removeEmptyValueFromObject } from "utils";
import { loadMomentLocale } from "utils/dates";
import { getLocaleJson } from "actions/global";
import { initializeI18 } from "utils/localization/config";
import { isWileyClientFECheck, enableSAP_DPA } from "handlers/features";
import debounce from "lodash/debounce";
import { logout } from "handlers/setters";
import { isUserPresetAndAuthenticated } from "containers/login/utils";
import { ToastContainer } from "react-toastify";
import SsoSessionExpiredModal from "components/SsoSessionExpiredModal";
import ProtectedRoute from "./ProtectedRoute";

const NavWrapper = lazy(() => import("./navWrapper"));
const CustomerLoginRedirect = lazy(
  () => import("containers/customer-login/login")
);
const VendorLoginRedirect = lazy(
  () => import("containers/vendors/invoicing-portal/login")
);
const ProtectedNavWrapper = (props) => (
  <Suspense fallback={null}>
    <NavWrapper {...props} />
  </Suspense>
);
const ApplicationAnnouncementBanner = lazy(
  () =>
    import(
      "components/ApplicationAnnouncementBanner/ApplicationAnnouncementBanner"
    )
);
const GuestCheckout = lazy(
  () => import("containers/guest_checkout/GuestCheckout")
);

const openRoutes = [
  "/customer/rel/",
  "receivables",
  "payables",
  "/settings/profile",
  "/vendors/rel/",
]; // routes which are open for customer & vendor

const INACTIVE_TIMEOUT_IN_MIN = 30; // 30 mins
export const LAST_ACTIVITY_TIME = "lastActivityTime";
export const LOCAL_INACTIVE_TIMEOUT_IN_MIN = "localInactivityTimeoutIn";

const saveLastActivityTimeInLclStrg = (time) =>
  localStorage.setItem(LAST_ACTIVITY_TIME, time);

const getLastActivityTimeFrmLclStrg = () => {
  const timeout = localStorage.getItem(LAST_ACTIVITY_TIME);
  if (timeout) {
    return Number(timeout);
  }
  return null;
};

let isAfterWarnEvent = false;
let API_IN_PROGRESS = false;
const refreshTokenTimeout = 300000; // 5 mins
const getOptionalModule = (result, moduleName) => {
  if (result.status === "rejected") {
    console.error(
      `[index routes] Failed to load optional module ${moduleName}:`,
      result.reason
    );
    return null;
  }
  return result.value;
};

function Root() {
  const [path] = useState(() => window.location.pathname);
  const [announcement, setAnnouncement] = useState(null);
  const dispatch = useDispatch();
  const store = useStore();
  const instanceRef = useRef(null);

  if (!instanceRef.current) {
    instanceRef.current = {
      events: ["load", "click", "keypress"],
      debouncedEvents: ["scroll", "mousemove", "mousedown"],
      minTimeout: null,
      warnTimeout: null,
      isTokenRefreshedOnce: false,
      lastStorageUpdateTime: null,
      uxActions: null,
      getNewToken: null,
      onPingUserLoaded: null,
      pingUserManager: null,
    };
  }
  const instance = instanceRef.current;

  const getInactiveTimeoutMs = () => {
    const baseTimeoutInMin =
      fe_config.MODE === "DEV" &&
      localStorage.getItem(LOCAL_INACTIVE_TIMEOUT_IN_MIN)
        ? Number(localStorage.getItem(LOCAL_INACTIVE_TIMEOUT_IN_MIN))
        : INACTIVE_TIMEOUT_IN_MIN;

    return baseTimeoutInMin * 60 * 1000;
  };

  const handleWindowFocus = async () => {
    const INACTIVE_TIMEOUT = getInactiveTimeoutMs();

    const currentTime = Date.now();

    const lastActivityTimestamp = getLastActivityTimeFrmLclStrg();

    if (
      lastActivityTimestamp &&
      currentTime - lastActivityTimestamp >= INACTIVE_TIMEOUT
    ) {
      if (TokenManager.isPingAuthorizationEnabled()) {
        const { getActiveUserManager } = await import("utils/oidcUserManager");
        const user = await getActiveUserManager().getUser();
        if (user && !user.expired) {
          const now = Math.floor(Date.now() / 1000);
          const tokenExpiry = user.expires_at;
          if (now < tokenExpiry) {
            API_IN_PROGRESS = true;
            logout();
            localStorage.removeItem(LAST_ACTIVITY_TIME);
            return;
          }
        }
        return;
      } else if (!TokenManager.get()) {
        return;
      }

      API_IN_PROGRESS = true;
      logout();
      localStorage.removeItem(LAST_ACTIVITY_TIME);
    } else {
      saveLastActivityTimeInLclStrg(currentTime);
      instance.lastStorageUpdateTime = currentTime;
    }
  };

  const refreshToken = (flag) => {
    if (TokenManager.isPingAuthorizationEnabled()) {
      return;
    }
    if (isAfterWarnEvent && !get(store.getState(), "header.refresh_header")) {
      if (API_IN_PROGRESS) {
        return;
      }
      if (!instance.getNewToken || !instance.uxActions) {
        console.error(
          "Skipping token refresh because refresh dependencies are not initialized"
        );
        return;
      }
      isAfterWarnEvent = false;
      API_IN_PROGRESS = true;
      const clientId = TokenManager.getClientId();
      const corpId = TokenManager.getCorporateEntityId();
      const hierarchical_corporate_entity_list_context = localStorage.getItem(
        "hierarchical_corporate_entity_list_context"
      );
      const headers = {
        client_id_context: clientId,
      };

      if (TokenManager.getClientCustomerRelId()) {
        headers["client_customer_rel_id_context"] =
          TokenManager.getClientCustomerRelId();
      }
      if (TokenManager.getClientSupplierRelId()) {
        headers["client_supplier_rel_id_context"] =
          TokenManager.getClientSupplierRelId();
      }
      if (TokenManager.getARrepContext()) {
        headers["client_customer_ar_rep_list"] = TokenManager.getARrepContext();
      }
      if (
        hierarchical_corporate_entity_list_context &&
        hierarchical_corporate_entity_list_context !== "null"
      ) {
        headers["hierarchical_corporate_entity_list_context"] =
          hierarchical_corporate_entity_list_context;
      }
      if (corpId) {
        headers["corporate_entity_id_context"] = corpId;
      }

      instance.getNewToken(
        { headers: removeEmptyValueFromObject(headers) },
        () => {
          API_IN_PROGRESS = false;
          isAfterWarnEvent = false;
          if (!instance.isTokenRefreshedOnce) {
            clearSetTimeout();
          }
          instance.uxActions.syncUserSettings(false);
          setRefreshTimeout(flag);
        }
      );
    }
  };

  const validateTokenTimeout = () => {
    isAfterWarnEvent = true;

    const isBrowserInactive = document.visibilityState === "hidden";

    if (!instance.isTokenRefreshedOnce) {
      if (isBrowserInactive) {
        const now = Date.now();
        saveLastActivityTimeInLclStrg(now);
        instance.lastStorageUpdateTime = now;
        setRefreshTimeout(true);
      }
    }
  };

  const setRefreshTimeout = (flag) => {
    API_IN_PROGRESS = false;
    isAfterWarnEvent = false;
    const currentTime = Math.round(new Date().getTime());

    if (document.hasFocus()) {
      const STORAGE_UPDATE_THROTTLE = 60000;
      if (
        !instance.lastStorageUpdateTime ||
        currentTime - instance.lastStorageUpdateTime >= STORAGE_UPDATE_THROTTLE
      ) {
        saveLastActivityTimeInLclStrg(currentTime);
        instance.lastStorageUpdateTime = currentTime;
      }
    }

    if (TokenManager.isPingAuthorizationEnabled()) return;

    const tokenExpiryTime = TokenManager.getExpiredAt();
    if (!tokenExpiryTime) {
      return;
    }
    if (!instance.getNewToken || !instance.uxActions) {
      return;
    }
    const reduceRefreshTime =
      fe_config.MODE === "DEV" && localStorage.getItem("refreshTokenTime")
        ? Number(localStorage.getItem("refreshTokenTime")) * 60 * 1000
        : 0;

    const REFRESH_TOKEN_TIME = reduceRefreshTime
      ? reduceRefreshTime
      : refreshTokenTimeout;

    const warnTime = tokenExpiryTime - currentTime - REFRESH_TOKEN_TIME;

    if (!instance.warnTimeout) {
      instance.warnTimeout = setTimeout(() => {
        isAfterWarnEvent = true;
        refreshToken(true);
        instance.isTokenRefreshedOnce = true;
      }, warnTime);
    }

    if (typeof flag === "boolean") {
      return;
    }

    if (instance.minTimeout) clearTimeout(instance.minTimeout);

    instance.isTokenRefreshedOnce = false;

    instance.minTimeout = setTimeout(() => {
      validateTokenTimeout();
    }, refreshTokenTimeout);
  };

  const logClickEvents = (e) => {
    const dataAttributeValue = get(e, "target.dataset.tlabel", "");
    const eventObj = {
      clicked_element:
        dataAttributeValue ||
        e.target.outerText ||
        e.target.textContent ||
        e.target.outerHTML,
    };
    const existingElement =
      localStorage.getItem("lastAction") &&
      JSON.parse(localStorage.getItem("lastAction"));

    if (
      eventObj.clicked_element !==
      (existingElement && existingElement.clicked_element)
    ) {
      const message = `Clicked on ${eventObj.clicked_element}`;
      trackEvent(message, "click");
    }
    localStorage.setItem("lastAction", JSON.stringify(eventObj));
  };

  const storageChange = (event) => {
    if (event.key === "REFRESHED") {
      window.location.reload(true);
    }
    if (event.key === "loggedout") {
      if (TokenManager.isPingAuthorizationEnabled()) {
        return;
      }
      window.sessionStorage.setItem("oldUrl", window.location.href);
      window.sessionStorage.setItem("oldUser", event.newValue);
      window.location.pathname !== "/login" && window.open("/login", "_self");
    }
    if (event.key === "loggedin") {
      if (event.newValue === window.sessionStorage.getItem("oldUser")) {
        const URL = window.sessionStorage.getItem("oldUrl");
        if (URL) {
          window.open(URL, "_self");
        }
      } else {
        window.close();
      }
    }

    if (event.key === LAST_ACTIVITY_TIME) {
      const lastActivityTime = Number(event.newValue);
      const currentTime = Date.now();
      const INACTIVE_TIMEOUT = getInactiveTimeoutMs();

      if (
        lastActivityTime &&
        currentTime - lastActivityTime <= INACTIVE_TIMEOUT
      ) {
        setRefreshTimeout(true);
      }
    }
  };

  const clearSetTimeout = () => {
    if (instance.warnTimeout) {
      clearTimeout(instance.warnTimeout);
      instance.warnTimeout = null;
    }
    if (instance.minTimeout) {
      clearTimeout(instance.minTimeout);
      instance.minTimeout = null;
    }
  };

  // Keep startup work and global listeners mount-scoped; Redux updates must not replay them.
  useEffect(() => {
    let isMounted = true;

    window.addEventListener("storage", storageChange, false);
    window.addEventListener("focus", handleWindowFocus);
    instance.debouncedActivityHandler = debounce(setRefreshTimeout, 800);
    instance.events.forEach((eventName) =>
      window.addEventListener(eventName, setRefreshTimeout)
    );
    instance.debouncedEvents.forEach((eventName) =>
      window.addEventListener(eventName, instance.debouncedActivityHandler)
    );

    loadMomentLocale(localStorage.getItem("locale") || "en").catch((error) => {
      console.error("Failed to load moment locale:", error);
    });
    isUserPresetAndAuthenticated()
      .then(async (isAuthenticated) => {
        if (isAuthenticated) {
          if (!isMounted) return;
          const [
            uxActions,
            { getActiveUserManager },
            { default: getNewToken },
          ] = await Promise.all([
            import("handlers/ux"),
            import("utils/oidcUserManager"),
            import("utils/token"),
          ]);
          if (!isMounted) return;
          instance.uxActions = uxActions;
          instance.getNewToken = getNewToken;

          // Use delegated click listener for better performance
          document.addEventListener("click", logClickEvents, true);
          setRefreshTimeout();

          const optionalImports = await Promise.allSettled([
            import("actions/user"),
            import("containers/settings/action"),
            import("actions/client"),
            import("containers/dashboard/TaskManagement/actions"),
            import("containers/support/api"),
            import("components/Tag"),
            import("containers/settings/company/dispute_reason/actions"),
            import("utils/createScriptTag"),
            import("containers/settings/payables/actions"),
            import("containers/settings/receivables/ARworkflow/actions"),
            import("containers/customers/listnew/actions"),
            import("containers/customers/details/actions"),
          ]);
          if (!isMounted) return;
          const [
            userActions,
            settingsActions,
            clientActions,
            taskActions,
            supportApi,
            tagActions,
            disputeActions,
            scriptTagActions,
            payablesActions,
            receivablesActions,
            customerListActions,
            customerDetailsActions,
          ] = optionalImports.map((result, index) =>
            getOptionalModule(
              result,
              [
                "actions/user",
                "containers/settings/action",
                "actions/client",
                "containers/dashboard/TaskManagement/actions",
                "containers/support/api",
                "components/Tag",
                "containers/settings/company/dispute_reason/actions",
                "utils/createScriptTag",
                "containers/settings/payables/actions",
                "containers/settings/receivables/ARworkflow/actions",
                "containers/customers/listnew/actions",
                "containers/customers/details/actions",
              ][index]
            )
          );

          if (!get(store.getState(), "user.clients.length", 0) && userActions) {
            userActions.getUserSettings(userActions.refreshClients);
          }
          if (settingsActions) {
            settingsActions.getConfigSettings();
          }
          if (supportApi) {
            dispatch(supportApi.getConfigVariables());
          }
          if (clientActions) {
            dispatch(clientActions.getClientData());
            dispatch(clientActions.getPayrollSettings());
          }
          if (taskActions) {
            dispatch(taskActions.getUsersList());
          }
          if (uxActions.uxempty()) {
            uxActions.ux_refresh();
          }
          tagActions?.refresh_tag();
          disputeActions?.getDisputeCodes();
          clientActions?.getPaymentAccountType().then(() => {
            if (!isMounted) return;
            scriptTagActions?.createScriptTag();
            if (TokenManager.isCustomer() && enableSAP_DPA()) {
              customerDetailsActions?.refetchCustomerInvoices &&
                [
                  "ar_invoice",
                  "ar_closed",
                  "customer_credit",
                  "ar_payment_history",
                ].forEach((invoiceType) => {
                  customerDetailsActions.refetchCustomerInvoices(invoiceType);
                });
            }
          });
          if (
            !TokenManager.isCustomer() &&
            !TokenManager.getIsAP_REP() &&
            !TokenManager.isReadOnlyARUser()
          ) {
            if (receivablesActions) {
              dispatch(receivablesActions.fetchEmailTemplateList());
            }
            payablesActions?.getInvoicingFields();
            if (!TokenManager.isVendor()) {
              clientActions?.getAccountTypes();
            }
          }
          if (
            !TokenManager.isCustomer() &&
            !TokenManager.isVendor() &&
            !TokenManager.getIsAP_REP()
          ) {
            // fetch sequence steps
            if (customerListActions) {
              customerListActions.fetchWorkflowSteps().then((res) => {
                if (isMounted) {
                  dispatch(
                    customerListActions.setWorkflowData({
                      workflows: res?.data?.result,
                    })
                  );
                }
              });
            }
            settingsActions?.getAgingBucket({ bucket_type: "ar" });
            settingsActions?.getAgingBucket({ bucket_type: "ap" });
          }
          uxActions.check_ux_properties();

          // For Ping SSO: drive TokenManager sync and syncUserSettings
          // via oidc-client-ts userLoaded event instead of warnTimeout cycle
          if (TokenManager.isPingAuthorizationEnabled()) {
            instance.onPingUserLoaded = (user) => {
              // Only sync expiry — username/userid are set at login and on context
              // switches via auth/verify; OIDC profile claims (sub, preferred_username)
              // differ from the app's internal values and must not overwrite them.
              TokenManager.setExpiredAt(user.expires_at * 1000);
              // Pass false to skip ux_refresh() — OIDC renewal does not change user
              // context, so re-fetching UX settings is unnecessary and would dispatch
              // SET_UX causing page components to re-render and re-fetch their data.
              uxActions.syncUserSettings(false);
            };
            instance.pingUserManager = getActiveUserManager();
            instance.pingUserManager.events.addUserLoaded(
              instance.onPingUserLoaded
            );
          }
        }
        // ...existing code...
      })
      .catch((error) => {
        console.error(
          "[index routes]Error fetching user authentication: ",
          error
        );
      });

    dispatch(getLocaleJson(localStorage.getItem("locale") || "en")).then(
      (translations) => {
        if (!isMounted) return;
        initializeI18({
          [localStorage.getItem("locale") || "en"]: { translations },
        });
      }
    );

    return () => {
      isMounted = false;
      clearSetTimeout();
      if (instance.onPingUserLoaded) {
        instance.pingUserManager?.events.removeUserLoaded(
          instance.onPingUserLoaded
        );
        instance.onPingUserLoaded = null;
        instance.pingUserManager = null;
      }
      instance.events.forEach((eventName) =>
        window.removeEventListener(eventName, setRefreshTimeout)
      );
      instance.debouncedEvents.forEach((eventName) =>
        window.removeEventListener(eventName, instance.debouncedActivityHandler)
      );
      instance.debouncedActivityHandler?.cancel();
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("storage", storageChange);
      document.removeEventListener("click", logClickEvents, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openRouteAvailable = openRoutes.some((d) => path.includes(d))
    ? path
    : "";
  const isVendorRouteAvailable = openRouteAvailable.includes("vendors");

  // Check if URL has a token parameter (indicates email-link token-based login)
  const hasTokenParam = window.location.search.includes("token=");

  // url contains login or signup or forgot-password
  return (
    <ErrorBoundary>
      <ToastContainer stacked={isWileyClientFECheck()} />
      <ConfirmationBox />
      <SsoSessionExpiredModal />
      <Router>
        <Switch>
          <Route exact path="/login" component={login} />
          <Route exact path="/sso-login" component={SsoLogin} />
          <Route
            exact
            path="/forgot-password"
            render={(props) => (
              <ForgotPassword operation="forgot-password" {...props} />
            )}
          />
          <Route
            exact
            path="/user-signup"
            render={(props) => <ForgotPassword text="user-signup" {...props} />}
          />
          <Route
            exact
            path="/token-expired"
            render={(props) => (
              <ForgotPassword tokenExpired={true} {...props} />
            )}
          />
          <Route exact path="/reset-password" component={resetPassword} />
          {Object.values(Constants.GuestLoginRouteMap).map(
            (guestRoute, index) => (
              <Route
                key={index}
                exact
                path={guestRoute.route}
                render={(compProps) => (
                  <>
                    <Suspense fallback={null}>
                      <ApplicationAnnouncementBanner
                        announcement={announcement}
                        onDismiss={() => setAnnouncement(null)}
                      />
                      <GuestCheckout
                        {...compProps}
                        client_id={guestRoute[fe_config.MODE]}
                        logo_url={guestRoute.logo_url}
                      />
                    </Suspense>
                  </>
                )}
              />
            )
          )}
          {!TokenManager.get() &&
            (!TokenManager.isPingAuthorizationEnabled() || hasTokenParam) &&
            openRouteAvailable && (
              <Route
                exact
                path={openRouteAvailable}
                render={(routeProps) => (
                  <Suspense fallback={null}>
                    {isVendorRouteAvailable ? (
                      <VendorLoginRedirect {...routeProps} />
                    ) : (
                      <CustomerLoginRedirect {...routeProps} />
                    )}
                  </Suspense>
                )}
              />
            )}
          <ProtectedRoute path="/" component={ProtectedNavWrapper} />
        </Switch>
      </Router>
    </ErrorBoundary>
  );
}

export default Root;
